import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  FaEnvelope, FaUser, FaClipboardList, FaTags, 
  FaSpinner, FaPaperPlane, FaSave, FaTimes, 
  FaSearch, FaFilter, FaTrash, FaEye, FaChartBar,
  FaBrain
} from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PathologyCasesManager = () => {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    fetchCases();
  }, [filterStatus]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/pathology/cases${params}`);
      if (response.data?.success) {
        setCases(response.data.cases || []);
        setStats(response.data.stats || { total: 0, draft: 0, sent: 0, closed: 0 });
      }
    } catch (error) {
      console.error('Error fetching pathology cases:', error);
      toast.error('Error al cargar los casos de patología');
    } finally {
      setLoading(false);
    }
  };

  const openCaseModal = (caseData) => {
    setSelectedCase(caseData);
    setEditingDiagnosis({
      subject: caseData.draft_subject || '',
      body: caseData.draft_body || ''
    });
    setShowModal(true);
  };

  const saveDraft = async () => {
    if (!selectedCase) return;
    
    setSavingDraft(true);
    try {
      await axios.put(`${API}/pathology/case/${selectedCase.id}`, {
        draft_subject: editingDiagnosis.subject,
        draft_body: editingDiagnosis.body
      });
      toast.success('Borrador guardado');
      fetchCases();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Error al guardar el borrador');
    } finally {
      setSavingDraft(false);
    }
  };

  const sendDiagnosisEmail = async () => {
    if (!selectedCase) return;
    
    if (!editingDiagnosis.body.trim()) {
      toast.error('El cuerpo del email no puede estar vacío');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/pathology/send-diagnosis`, {
        case_id: selectedCase.id,
        subject: editingDiagnosis.subject,
        body: editingDiagnosis.body
      });
      
      if (response.data?.success) {
        toast.success(`Email enviado exitosamente a ${selectedCase.user_email}`);
        setShowModal(false);
        setSelectedCase(null);
        fetchCases();
      }
    } catch (error) {
      console.error('Error sending diagnosis email:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const deleteCase = async (caseId) => {
    if (!window.confirm('¿Estás seguro de eliminar este caso?')) return;
    
    try {
      await axios.delete(`${API}/pathology/case/${caseId}`);
      toast.success('Caso eliminado');
      fetchCases();
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Error al eliminar el caso');
    }
  };

  const generateTemplate = async () => {
    if (!selectedCase) return;
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/pathology/generate-clinical-email`, {
        user_name: selectedCase.user_name || '',
        user_email: selectedCase.user_email || '',
        condition_ids: selectedCase.pathologies_selected || selectedCase.pathologies || [],
        clinical_summary: selectedCase.clinical_summary || null
      });
      if (response.data?.success) {
        const content = response.data.email_content;
        setEditingDiagnosis(prev => ({
          ...prev,
          subject: content.subject || prev.subject,
          body: content.body || prev.body
        }));
        toast.success('Plantilla generada correctamente');
      }
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Error al generar la plantilla');
    } finally {
      setGeneratingAI(false);
    }
  };

  const generateWithAI = async () => {
    if (!selectedCase) return;
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${API}/pathology/generate-ai-email`, {
        user_name: selectedCase.user_name || '',
        condition_ids: selectedCase.pathologies_selected || selectedCase.pathologies || [],
        clinical_summary: selectedCase.clinical_summary || null,
        tone: "profesional y cálido"
      });
      if (response.data?.success) {
        const content = response.data.email_content;
        setEditingDiagnosis(prev => ({
          ...prev,
          subject: content.subject || prev.subject,
          body: content.body || prev.body
        }));
        toast.success('Email generado con IA exitosamente');
      }
    } catch (error) {
      console.error('Error generating AI email:', error);
      toast.error('Error al generar email con IA');
    } finally {
      setGeneratingAI(false);
    }
  };

  const filteredCases = cases.filter(c => 
    searchTerm === '' || 
    c.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-yellow-500/30 text-yellow-300 border-yellow-500/50">Borrador</Badge>;
      case 'sent':
        return <Badge className="bg-green-500/30 text-green-300 border-green-500/50">Enviado</Badge>;
      case 'closed':
        return <Badge className="bg-gray-500/30 text-gray-300 border-gray-500/50">Cerrado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="pathology-cases-manager">
      {/* Header con estadísticas */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <FaChartBar className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-sm text-gray-400">Total Casos</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-yellow-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <FaClipboardList className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.draft}</p>
              <p className="text-sm text-gray-400">Borradores</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-500/20">
              <FaEnvelope className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.sent}</p>
              <p className="text-sm text-gray-400">Enviados</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-600/20 to-slate-600/20 border-gray-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gray-500/20">
              <FaTags className="w-6 h-6 text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.closed}</p>
              <p className="text-sm text-gray-400">Cerrados</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Buscar por email o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-600"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                className={filterStatus === 'all' ? 'bg-blue-600' : ''}
              >
                Todos
              </Button>
              <Button
                size="sm"
                variant={filterStatus === 'draft' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('draft')}
                className={filterStatus === 'draft' ? 'bg-yellow-600' : ''}
              >
                Borradores
              </Button>
              <Button
                size="sm"
                variant={filterStatus === 'sent' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('sent')}
                className={filterStatus === 'sent' ? 'bg-green-600' : ''}
              >
                Enviados
              </Button>
            </div>
            
            <Button
              size="sm"
              onClick={fetchCases}
              variant="outline"
              className="border-gray-600"
            >
              <FaFilter className="mr-2" /> Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de casos */}
      <Card className="bg-gray-900/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FaClipboardList className="text-pink-400" />
            Casos de Diagnóstico ({filteredCases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <FaSpinner className="animate-spin text-pink-500 text-3xl" />
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FaClipboardList className="mx-auto text-4xl mb-3 opacity-50" />
              <p>No hay casos de patología registrados</p>
              <p className="text-sm mt-1">Los casos se generan cuando procesas patologías de un usuario en el Generador Offline</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCases.map((caseItem) => (
                <div 
                  key={caseItem.id}
                  className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-pink-500/50 transition-colors"
                  data-testid={`pathology-case-${caseItem.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FaUser className="text-gray-400" />
                        <span className="font-medium text-white">{caseItem.user_name || 'Sin nombre'}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-400 text-sm">{caseItem.user_email}</span>
                        {getStatusBadge(caseItem.status)}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-2">
                        {caseItem.pathologies_selected?.map((p, i) => (
                          <Badge key={i} className="bg-pink-500/20 text-pink-300 border-pink-500/50 text-xs">
                            {p}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mt-2">
                        {caseItem.tags?.map((t, i) => (
                          <Badge key={i} variant="outline" className="text-xs text-cyan-400 border-cyan-500/50">
                            {t}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-2">
                        Creado: {new Date(caseItem.created_at).toLocaleString('es-CO')}
                        {caseItem.sent_at && (
                          <span className="ml-3">
                            Enviado: {new Date(caseItem.sent_at).toLocaleString('es-CO')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => openCaseModal(caseItem)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        <FaEye className="mr-1" /> Ver
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteCase(caseItem.id)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalle/edición */}
      {showModal && selectedCase && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl border border-teal-500/50 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600/30 to-emerald-600/30 px-6 py-4 border-b border-teal-500/30 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FaEnvelope className="text-teal-400" />
                  Caso de Diagnóstico
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Para: <span className="text-teal-300">{selectedCase.user_name}</span> ({selectedCase.user_email})
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white p-2"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Info del caso */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Patologías detectadas:</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCase.pathologies_selected?.map((p, i) => (
                    <Badge key={i} className="bg-pink-500/30 text-pink-300 border-pink-500/50">
                      {p}
                    </Badge>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Tags:</span>
                  {selectedCase.tags?.map((t, i) => (
                    <Badge key={i} variant="outline" className="text-xs text-cyan-400 border-cyan-500/50">
                      {t}
                    </Badge>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500">Formularios activados:</span>
                  {selectedCase.forms_details?.map((f, i) => (
                    <Badge key={i} variant="outline" className="text-xs text-emerald-400 border-emerald-500/50">
                      {f.form_name}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Botones Generar con IA */}
              <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-lg p-3 border border-emerald-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-300 font-medium">Motor Narrativo</p>
                    <p className="text-xs text-gray-400">Generar o regenerar mensaje</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={generateTemplate}
                      disabled={generatingAI}
                      className="bg-gray-700 hover:bg-gray-600 text-white text-xs"
                      data-testid="pcm-generate-template-btn"
                    >
                      {generatingAI ? (
                        <FaSpinner className="mr-1 animate-spin" />
                      ) : (
                        <FaClipboardList className="mr-1" />
                      )}
                      Plantilla
                    </Button>
                    <Button
                      size="sm"
                      onClick={generateWithAI}
                      disabled={generatingAI}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs"
                      data-testid="pcm-generate-ai-btn"
                    >
                      {generatingAI ? (
                        <FaSpinner className="mr-1 animate-spin" />
                      ) : (
                        <FaBrain className="mr-1" />
                      )}
                      Generar con IA
                    </Button>
                  </div>
                </div>
              </div>

              {/* Editor de Subject */}
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1 block">Asunto del email:</label>
                <Input
                  value={editingDiagnosis.subject}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, subject: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Asunto del correo..."
                />
              </div>
              
              {/* Editor de Body */}
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1 block">
                  Contenido del email:
                  <span className="text-xs text-gray-500 ml-2">(Puedes editar antes de enviar)</span>
                </label>
                <Textarea
                  value={editingDiagnosis.body}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, body: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white min-h-[300px] font-mono text-sm"
                  placeholder="Contenido del correo de diagnóstico..."
                />
              </div>
              
              {/* Status */}
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Estado: {getStatusBadge(selectedCase.status)}</span>
                {selectedCase.sent_at && (
                  <span>Enviado: {new Date(selectedCase.sent_at).toLocaleString('es-CO')}</span>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-700 px-6 py-4 bg-gray-800/50 flex justify-between items-center">
              <Button
                variant="outline"
                onClick={saveDraft}
                disabled={savingDraft}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                {savingDraft ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
                Guardar Borrador
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="border-gray-600 text-gray-400 hover:bg-gray-700"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={sendDiagnosisEmail}
                  disabled={sendingEmail || selectedCase.status === 'sent'}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                >
                  {sendingEmail ? (
                    <><FaSpinner className="mr-2 animate-spin" /> Enviando...</>
                  ) : selectedCase.status === 'sent' ? (
                    <><FaEnvelope className="mr-2" /> Ya enviado</>
                  ) : (
                    <><FaPaperPlane className="mr-2" /> Enviar Email</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathologyCasesManager;
